import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { useBatchCurriculum } from '@/lib/hooks/useLms'
import type { ModuleWithProgress } from '@/lib/api/lms'

/**
 * A module isn't routable on its own, so a milestone links to the topic the
 * student would actually resume at: the first unfinished one, in curriculum
 * order. Returns undefined when the module carries no topics (older API
 * responses), which keeps the row rendering as plain text instead of a
 * link to nowhere.
 */
function nextTopicId(module: ModuleWithProgress): string | undefined {
  return [...(module.topics ?? [])]
    .sort((a, b) => a.sort_order - b.sort_order)
    .find((t) => t.status !== 'completed')?.id
}

export function UpcomingMilestonesCard({ batchId }: { batchId: string }) {
  const { data: curriculum, isLoading } = useBatchCurriculum(batchId)

  if (isLoading || !curriculum) return null

  const upcoming = curriculum.modules
    .filter((m) => m.topic_count > 0 && m.completed_count < m.topic_count)
    .slice(0, 3)

  return (
    <section className="rounded-xl border border-[#e8e4df] bg-white p-6">
      <p className="mb-4 text-[11px] font-bold uppercase tracking-wide text-[#9a9589]">
        Upcoming Milestones
      </p>
      {upcoming.length === 0 ? (
        <p className="text-sm text-[#9a9589]">All modules complete 🎉</p>
      ) : (
        <ol className="space-y-3">
          {upcoming.map((m, i) => {
            const isInProgress = m.completed_count > 0 && m.completed_count < m.topic_count
            const topicId = nextTopicId(m)
            return (
              <li key={m.id} className="flex items-center gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#f2f0ed] text-xs font-bold text-[#9a9589]">
                  {i + 1}
                </span>
                {topicId ? (
                  <Link
                    to="/courses/$batchId/topics/$topicId"
                    params={{ batchId, topicId }}
                    className="flex-1 truncate text-sm font-medium text-[#1a1714] transition-colors hover:text-[#e8660d]"
                  >
                    {m.title}
                  </Link>
                ) : (
                  <span className="flex-1 text-sm font-medium text-[#1a1714]">{m.title}</span>
                )}
                {isInProgress && (
                  <Badge variant="secondary" className="text-[10px]">In Progress</Badge>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
